import { useRef } from 'react';

import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Paper from '@zenbusiness/zui/Paper';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

import AdditionalPaymentInfo from '#client/components/AdditionalPaymentInfo';
import FormInfoModal from '#client/components/FormInfoModal';
import { ActionInfoButtonsGrid } from '#client/components/Grid';
import DetailWrapper from '#client/components/TransactionDetailModal/components/DetailWrapper';
import { INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS } from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import { formatAccountNumber } from '#client/utils/text';

import DebitCardDetail from './DebitCardDetail';
import DebitCardPaymentDetail from './DebitCardPaymentDetail';
import { ButtonStyled, PrintedBox } from './DebitCardPaymentDetailModal.styles';

export default function DebitCardPaymentDetailModal({
  uuid,
  amount,
  routineStatus,
  createdDatetime,
  updatedDatetime,
  cardInfo,
  handleClose
}) {
  const contentRef = useRef(null);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  const isCompleted = routineStatus === INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.COMPLETED;
  const {
    cardCompany,
    cardLastFourDigits,
    cardBillingAddress,
    cardHolderFirstName,
    cardHolderLastName
  } = cardInfo ?? {};
  const formatCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits ?? '',
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const title = `From card ${formatCardNumber} via Astra`;

  return (
    <FormInfoModal
      borderRadius={isDesktop ? '16px' : '0'}
      fullScreen={!isDesktop}
      handleClose={handleClose}
      title={title}
    >
      <DialogContent sx={{ padding: 0 }}>
        <DetailWrapper>
          <PrintedBox ref={contentRef}>
            <Paper variant="outlined">
              <DebitCardPaymentDetail
                amount={amount}
                createdAt={isCompleted ? undefined : createdDatetime}
                updateAt={isCompleted ? updatedDatetime : undefined}
              />
              <DebitCardDetail
                cardBillingAddress={cardBillingAddress}
                cardCompany={cardCompany}
                cardHolderFirstName={cardHolderFirstName}
                cardHolderLastName={cardHolderLastName}
                cardLastFourDigits={cardLastFourDigits}
              />
            </Paper>
          </PrintedBox>
          <AdditionalPaymentInfo
            handlePrint={handlePrint}
            id={uuid}
            isPending={false}
          />
        </DetailWrapper>
      </DialogContent>
      {!isDesktop && (
        <ActionInfoButtonsGrid>
          <Box>
            <ButtonStyled
              color="primary"
              onClick={handleClose}
              size="large"
              variant="text"
            >
              Close
            </ButtonStyled>
          </Box>
        </ActionInfoButtonsGrid>
      )}
    </FormInfoModal>
  );
}

DebitCardPaymentDetailModal.propTypes = {
  uuid: PropTypes.string.isRequired,
  routineStatus: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  createdDatetime: PropTypes.string,
  updatedDatetime: PropTypes.string,
  cardInfo: PropTypes.shape({
    cardCompany: PropTypes.string,
    cardLastFourDigits: PropTypes.string,
    cardHolderFirstName: PropTypes.string,
    cardHolderLastName: PropTypes.string,
    cardBillingAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      state: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string
    })
  }),
  handleClose: PropTypes.func
};
